import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Icon, Typography } from '@/components';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { useNotifications } from '@/features/notifications';

import { useUpdateCompanyMutation } from '../../api';
import { CompanySchema } from '../../types';
import { InvoicingDrawer } from './invoicing-drawer';
import { InvoicingFormFields } from './invoicing-form';

const FORM_ID = 'company-invoicing-form';

function Kv({ label, children, accent }: { label: string; children?: ReactNode; accent?: boolean }) {
  if (!children) return null;
  return (
    <div className="mt-4 first:mt-0">
      <Typography as="p" size="caption" color="secondary" weight="semibold" className="uppercase tracking-wide">
        {label}
      </Typography>
      <Typography as="p" size="body-2" color={accent ? 'primary-accent' : 'primary'} className="mt-0.5 break-words">
        {children}
      </Typography>
    </div>
  );
}

export type InvoicingCardProps = {
  readonly company: CompanySchema;
};

export function InvoicingCard({ company }: InvoicingCardProps) {
  const { t } = useTranslation(['settings']);
  const [editing, setEditing] = useState(false);
  const [updateCompany, { isLoading }] = useUpdateCompanyMutation();
  const { add } = useNotifications();

  const onSubmit = (data: InvoicingFormFields) => {
    updateCompany({ id: company.id, data })
      .unwrap()
      .then(() => {
        add({
          id: new Date().toString(),
          title: t('Company updated'),
          description: t('Your company has been updated'),
        });
        setEditing(false);
      })
      .catch(() => {
        add({
          id: new Date().toString(),
          title: t('ERROR'),
          description: t('There was an error updating your company. Please try again later.'),
        });
      });
  };

  const cityLine = [company.billing_postal_code, company.billing_city].filter(Boolean).join(' ').trim();
  const billingAddress = [company.billing_address, cityLine, company.billing_country].filter(Boolean).join(', ');
  const hasData = Boolean(
    company.registered_name ||
      company.vat_number ||
      billingAddress ||
      company.billing_aditional_info ||
      company.billing_email
  );

  return (
    <>
      <Card className="p-6 gap-3">
        <div className="mb-1 flex flex-wrap items-center justify-between gap-1">
          <Typography as="h3" color="primary" size="title" weight="semibold">
            {t('Invoicing data')}
          </Typography>
          {hasData ? (
            <Button size="sm" color="neutral" icon="edit" onClick={() => setEditing(true)}>
              {t('Edit')}
            </Button>
          ) : null}
        </div>

        <Badge variant="secondary" className="gap-1 rounded-full font-semibold">
          <Icon name="lock" className="h-3 w-3" />
          {t('Private')}
        </Badge>

        {hasData ? (
          <div className="mt-2">
            <Kv label={t('Registered company name')}>{company.registered_name}</Kv>
            <Kv label={t('VAT number')}>{company.vat_number}</Kv>
            <Kv label={t('Billing address')}>{billingAddress || null}</Kv>
            <Kv label={t('Billing email')} accent>
              {company.billing_email}
            </Kv>
            <Kv label={t('Additional info')}>{company.billing_aditional_info}</Kv>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-primary-700 mt-2 block text-left text-base font-medium hover:underline"
          >
            {t('Add your invoicing details')}
          </button>
        )}

        <Typography as="p" size="body-3" color="secondary" className="mt-1 border-t border-neutral-50 pt-2">
          {t('Used only on nTeaser invoices. Never shown to other companies on the platform.')}
        </Typography>
      </Card>

      <InvoicingDrawer
        formId={FORM_ID}
        company={company}
        isOpen={editing}
        isLoading={isLoading}
        onSubmit={onSubmit}
        onClose={() => setEditing(false)}
      />
    </>
  );
}
